import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'vp_lang';
const LanguageContext = createContext({ lang: 'en', isHi: false, setLang: () => {}, t: s => s });

const interpolate = (s, vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined ? m : vars[k])) : s);

// The Hindi dictionary (~110 KB) is its own chunk: English-only visitors never download it,
// Hindi users fetch it in parallel with the first screen (and the service worker caches it).
let HI = null;
let hiLoading = null;
export const loadHi = () => {
  if (HI) return Promise.resolve(HI);
  hiLoading = hiLoading || import('./hi').then((m) => { HI = m.hi; return HI; });
  return hiLoading;
};

export function translate(lang, key, vars) {
  const text = lang === 'hi' ? (HI?.[key] ?? key) : key;
  return interpolate(text, vars);
}

const readStored = () => {
  try { const v = localStorage.getItem(STORAGE_KEY); return v === 'hi' ? 'hi' : 'en'; } catch { return 'en'; }
};
if (readStored() === 'hi') loadHi();

// Simple-Hindi / English switch. Phone remembers it (localStorage); logged-in users also
// get it saved on their account so it follows them to any device. Roles whose screens are
// fully translated START in Hindi (server `default`) until the person taps the toggle
// themselves — only a tap is recorded as `source: 'user'` and respected everywhere.
export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(readStored);
  const [dictReady, setDictReady] = useState(() => !!HI);
  useEffect(() => {
    if (lang !== 'hi' || HI) return undefined;
    let alive = true;
    loadHi().then(() => { if (alive) setDictReady(true); });
    return () => { alive = false; };
  }, [lang]);
  const syncedFor = useRef(null);
  const pendingChoice = useRef(null); // toggled on the Login page before signing in → counts as the person's own choice

  useEffect(() => {
    if (!user?.id || syncedFor.current === user.id) return;
    syncedFor.current = user.id;
    if (pendingChoice.current) {
      const v = pendingChoice.current;
      pendingChoice.current = null;
      axios.put(`${API}/auth/language`, { language: v, source: 'user' }).catch(() => {});
      return;
    }
    axios.get(`${API}/auth/language`).then(r => {
      const { language, source, default: roleDefault } = r.data || {};
      const chosen = source === 'user' && (language === 'hi' || language === 'en');
      const next = chosen ? language : (roleDefault === 'hi' ? 'hi' : 'en');
      setLangState(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }).catch(() => {});
  }, [user?.id]);

  const setLang = useCallback((next) => {
    const v = next === 'hi' ? 'hi' : 'en';
    setLangState(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
    if (user?.id) axios.put(`${API}/auth/language`, { language: v, source: 'user' }).catch(() => {});
    else pendingChoice.current = v;
  }, [user?.id]);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const value = useMemo(() => ({
    lang, isHi: lang === 'hi', setLang,
    t: (key, vars) => translate(lang, key, vars),
  }), [lang, setLang, dictReady]); // eslint-disable-line react-hooks/exhaustive-deps -- dictReady re-renders consumers once Hindi arrives

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useT = () => useContext(LanguageContext);
